/**
 * Simple encryption/decryption utility for URL parameters
 * Uses base64 encoding with a secret key for basic obfuscation
 * Note: For production, consider using a more secure encryption method
 */

const SECRET_KEY = 'frontdesk24_payment_secret_2024'; // Change this to your secret key

/**
 * Encrypts a string using simple XOR cipher with base64 encoding
 */
export function encrypt(data: string): string {
  try {
    // Convert data and key to bytes
    const dataBytes = new TextEncoder().encode(data);
    const keyBytes = new TextEncoder().encode(SECRET_KEY);
    
    // XOR encryption
    const encrypted = dataBytes.map((byte, index) => 
      byte ^ keyBytes[index % keyBytes.length]
    );
    
    // Convert to base64
    const base64 = btoa(String.fromCharCode(...encrypted));
    
    // URL-safe encoding (replace + with -, / with _, remove = padding)
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch (error) {
    console.error('Encryption error:', error);
    return data; // Return original if encryption fails
  }
}

/**
 * Decrypts an encrypted string
 */
export function decrypt(encryptedData: string): string {
  try {
    // Restore base64 padding and URL-safe characters
    let base64 = encryptedData.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    while (base64.length % 4) {
      base64 += '=';
    }
    
    // Decode from base64
    const encrypted = Uint8Array.from(
      atob(base64),
      c => c.charCodeAt(0)
    );
    
    // Get key bytes
    const keyBytes = new TextEncoder().encode(SECRET_KEY);
    
    // XOR decryption (XOR is symmetric)
    const decrypted = encrypted.map((byte, index) => 
      byte ^ keyBytes[index % keyBytes.length]
    );
    
    // Convert back to string
    return new TextDecoder().decode(decrypted);
  } catch (error) {
    console.error('Decryption error:', error);
    return encryptedData; // Return original if decryption fails
  }
}

/**
 * Encrypts customer data object
 */
export function encryptCustomerData(data: {
  phone?: string;
  email?: string;
  customerId?: string;
  name?: string;
}): string {
  const jsonString = JSON.stringify(data);
  return encrypt(jsonString);
}

/**
 * Decrypts customer data object
 */
export function decryptCustomerData(encryptedData: string): {
  phone?: string;
  email?: string;
  customerId?: string;
  name?: string;
} {
  try {
    const decrypted = decrypt(encryptedData);
    return JSON.parse(decrypted);
  } catch (error) {
    console.error('Customer data decryption error:', error);
    return {};
  }
}

